import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Mountain, Loader2, ShieldCheck, FileText, Download, Clock, RefreshCw } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

const FIELDS = [
  { k: 'sottoscritto', label: 'Il/La sottoscritto/a', ph: 'Nome e cognome' },
  { k: 'denominazione', label: 'Ragione sociale / Denominazione', ph: 'Denominazione' },
  { k: 'sede', label: 'Sede legale / Studio in', ph: 'Indirizzo e città' },
  { k: 'cf_piva', label: 'C.F. / P.IVA', ph: 'Codice fiscale o partita IVA' },
  { k: 'email', label: 'Email', ph: 'email@dominio.it' },
  { k: 'telefono', label: 'Telefono', ph: '+39 ...' },
];

function Shell({ subtitle, children }) {
  return (
    <div className="min-h-screen bg-background px-4 py-8 sm:px-6">
      <div className="mx-auto w-full max-w-5xl">
        <div className="mb-6 flex items-center gap-2.5">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground"><Mountain className="h-5 w-5" /></div>
          <div><p className="font-display text-lg font-bold">Sila Experiences</p><p className="text-xs text-muted-foreground">{subtitle}</p></div>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function StakeholderSubscription() {
  const { token } = useParams();
  const [state, setState] = useState('loading'); // loading|expired|completed|form|otp|notfound
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({});
  const [pdfVer, setPdfVer] = useState(0);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState('');
  const [confirm, setConfirm] = useState(false);

  const pdfUrl = `${BACKEND}/api/stakeholder-contract/${token}/pdf?v=${pdfVer}`;
  const signedUrl = `${BACKEND}/api/stakeholder-contract/${token}/signed-pdf`;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/stakeholder-contract/${token}`);
      setInfo(data);
      setForm(data.form_data || {});
      if (data.status === 'completed') setState('completed');
      else if (data.expired) setState('expired');
      else setState('form');
    } catch (e) {
      if (e.response?.status === 404) setState('notfound');
      else toast.error(formatApiError(e.response?.data?.detail) || 'Errore di caricamento');
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const isComplete = () => {
    const req = ['sottoscritto', 'qualifica', 'denominazione', 'sede', 'cf_piva', 'email', 'telefono', 'categoria', 'data_gg', 'data_mm'];
    return req.every((k) => String(form[k] || '').trim()) && (info?.categories || []).includes(form.categoria) && (info?.qualifiche || []).includes(form.qualifica);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/stakeholder-contract/${token}/form`, form);
      setPdfVer((v) => v + 1);
      toast.success('Dati salvati nel documento');
      setConfirm(false);
    } catch (e) {
      if (e.response?.status === 410) { setState('expired'); }
      toast.error(formatApiError(e.response?.data?.detail) || 'Errore salvataggio');
    } finally { setSaving(false); }
  };

  const requestOtp = async () => {
    setBusy(true);
    try {
      await api.put(`/stakeholder-contract/${token}/form`, form); // garantisce coerenza doc/otp
      setPdfVer((v) => v + 1);
      await api.post(`/stakeholder-contract/${token}/request-otp`);
      toast.success('Ti abbiamo inviato un codice via email (valido 5 minuti)');
      setCode('');
      setState('otp');
    } catch (e) {
      if (e.response?.status === 410) setState('expired');
      toast.error(formatApiError(e.response?.data?.detail) || 'Impossibile inviare il codice');
    } finally { setBusy(false); }
  };

  const doConfirm = async () => {
    setBusy(true);
    try {
      await api.post(`/stakeholder-contract/${token}/confirm`, { code });
      toast.success('Sottoscrizione completata');
      await load();
      setState('completed');
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'Verifica non riuscita');
    } finally { setBusy(false); }
  };

  const resend = async () => {
    setBusy(true);
    try {
      await api.post(`/stakeholder-contract/${token}/resend-invite`);
      toast.success('Ti abbiamo inviato un nuovo link via email');
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'Invio non riuscito');
    } finally { setBusy(false); }
  };

  if (state === 'loading') {
    return <Shell subtitle="Adesione partner"><div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" />Caricamento…</div></Shell>;
  }
  if (state === 'notfound') {
    return <Shell subtitle="Adesione partner"><Card className="p-8" data-testid="subscription-notfound"><h1 className="font-display text-2xl font-bold">Link non valido</h1><p className="mt-2 text-sm text-muted-foreground">Il link non è corretto o non esiste più.</p></Card></Shell>;
  }
  if (state === 'expired') {
    return (
      <Shell subtitle="Adesione partner">
        <Card className="p-8" data-testid="subscription-expired">
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-accent"><Clock className="h-6 w-6 text-primary" /></div>
          <h1 className="font-display text-2xl font-bold">Link scaduto</h1>
          <p className="mt-2 text-sm text-muted-foreground">Questo link di adesione non è più valido. Possiamo inviartene uno nuovo all'indirizzo email registrato.</p>
          <Button className="mt-5" onClick={resend} disabled={busy} data-testid="subscription-resend-button">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}Invia un nuovo link</Button>
        </Card>
      </Shell>
    );
  }
  if (state === 'completed') {
    return (
      <Shell subtitle="Adesione partner">
        <Card className="p-8 text-center" data-testid="subscription-completed">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-accent"><ShieldCheck className="h-7 w-7 text-primary" /></div>
          <h1 className="font-display text-2xl font-bold">Adesione sottoscritta</h1>
          <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">Grazie! Abbiamo registrato la sottoscrizione del tuo modulo di adesione. La tua richiesta sarà ora verificata dall'amministrazione per l'approvazione finale.</p>
          <a href={signedUrl} target="_blank" rel="noreferrer"><Button className="mt-6" data-testid="subscription-download-button"><Download className="mr-2 h-4 w-4" />Scarica il documento sottoscritto</Button></a>
        </Card>
      </Shell>
    );
  }

  // form / otp
  const complete = isComplete();
  return (
    <Shell subtitle="Completa la tua adesione">
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Colonna sinistra: modulo + OTP */}
        <div className="space-y-5">
          <Card className="space-y-4 p-5" data-testid="subscription-form">
            <div>
              <h1 className="font-display text-xl font-bold">Dati per la sottoscrizione</h1>
              <p className="text-sm text-muted-foreground">Compila i campi: compariranno nel documento accanto. Verifica l'anteprima prima di firmare.</p>
            </div>
            <div className="space-y-2">
              <Label>In qualità di</Label>
              <Select value={form.qualifica || ''} onValueChange={(v) => set('qualifica', v)}>
                <SelectTrigger data-testid="subscription-qualifica"><SelectValue placeholder="Seleziona la qualifica" /></SelectTrigger>
                <SelectContent>{(info.qualifiche || []).map((q) => <SelectItem key={q} value={q}>{q}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {FIELDS.map((f) => (
              <div key={f.k} className="space-y-2">
                <Label htmlFor={f.k}>{f.label}</Label>
                <Input id={f.k} value={form[f.k] || ''} onChange={(e) => set(f.k, e.target.value)} placeholder={f.ph} data-testid={`subscription-field-${f.k}`} disabled={state === 'otp'} />
              </div>
            ))}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label htmlFor="data_gg">Giorno</Label><Input id="data_gg" inputMode="numeric" maxLength={2} value={form.data_gg || ''} onChange={(e) => set('data_gg', e.target.value.replace(/\D/g, ''))} placeholder="GG" data-testid="subscription-field-data_gg" disabled={state === 'otp'} /></div>
              <div className="space-y-2"><Label htmlFor="data_mm">Mese</Label><Input id="data_mm" inputMode="numeric" maxLength={2} value={form.data_mm || ''} onChange={(e) => set('data_mm', e.target.value.replace(/\D/g, ''))} placeholder="MM" data-testid="subscription-field-data_mm" disabled={state === 'otp'} /></div>
            </div>
            <div className="space-y-2">
              <Label>Categoria di appartenenza</Label>
              <RadioGroup value={form.categoria || ''} onValueChange={(v) => set('categoria', v)} className="space-y-1.5" data-testid="subscription-categoria">
                {(info.categories || []).map((c) => (
                  <label key={c} className="flex cursor-pointer items-start gap-2 rounded-md border border-border p-2 text-sm hover:bg-accent" data-testid={`subscription-cat-${(info.categories || []).indexOf(c)}`}>
                    <RadioGroupItem value={c} className="mt-0.5" disabled={state === 'otp'} />
                    <span>{c}</span>
                  </label>
                ))}
              </RadioGroup>
            </div>
          </Card>

          {state === 'form' && (
            <Card className="space-y-3 p-5" data-testid="subscription-actions">
              <Button variant="outline" onClick={save} disabled={saving} className="w-full" data-testid="subscription-save-button">{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}Salva e aggiorna anteprima</Button>
              <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} className="mt-1" data-testid="subscription-confirm-checkbox" />
                <span>Confermo di voler sottoscrivere elettronicamente il presente modulo di adesione con le informazioni inserite.</span>
              </label>
              <Button onClick={requestOtp} disabled={!complete || !confirm || busy} className="w-full" data-testid="subscription-request-otp-button">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}Conferma e ricevi il codice</Button>
              {!complete && <p className="text-xs text-muted-foreground">Compila tutti i campi e seleziona qualifica e categoria per procedere.</p>}
            </Card>
          )}

          {state === 'otp' && (
            <Card className="space-y-3 p-5" data-testid="subscription-otp">
              <h2 className="font-display text-lg font-bold">Inserisci il codice</h2>
              <p className="text-sm text-muted-foreground">Abbiamo inviato un codice di 6 cifre alla tua email. È valido 5 minuti.</p>
              <Input inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} placeholder="______" className="text-center text-lg tracking-[0.4em]" data-testid="subscription-otp-input" />
              <Button onClick={doConfirm} disabled={code.length !== 6 || busy} className="w-full" data-testid="subscription-confirm-button">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}Sottoscrivi</Button>
              <Button variant="ghost" onClick={requestOtp} disabled={busy} className="w-full" data-testid="subscription-resend-otp-button">Invia di nuovo il codice</Button>
              <Button variant="ghost" onClick={() => setState('form')} disabled={busy} className="w-full">Modifica i dati</Button>
              <p className="text-xs text-muted-foreground">Sottoscrizione elettronica tramite codice OTP via email. Non costituisce firma elettronica qualificata.</p>
            </Card>
          )}
        </div>

        {/* Colonna destra: anteprima PDF (tutte le pagine) */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium"><FileText className="h-4 w-4" />Anteprima del documento (tutte le pagine)</div>
          <iframe title="Modulo di adesione" src={pdfUrl} className="h-[760px] w-full rounded-md border border-border bg-white" data-testid="subscription-pdf-frame" />
        </div>
      </div>
    </Shell>
  );
}
